import { forwardRef } from 'react'

import styled from '@emotion/styled'

import { ExpandCenteredIcon } from '@arcade/web/icons/react'

import FormattedInput, { BaseFormattedInputProps } from '../Arcade/Input/FormattedInput'
import { Flag } from './Flag'

export const SUPPORTED_REGIONS = ['GB', 'US'] as const
type SupportedRegion = (typeof SUPPORTED_REGIONS)[number]

export type PhoneInputProps = BaseFormattedInputProps & {
  showRegionCode?: boolean
  regionCode?: SupportedRegion
  regionCodeAriaLabel?: string
  onClickRegionCode?: () => void
}

// Padding to maintain 1rem gap between region selector and input value
// given the varying widths of region selector with different region codes
const inputPaddingMap = {
  US: 112,
  GB: 128,
} as const

const InputContainer = styled.div<{ region?: SupportedRegion; showPrefixInteraction: boolean }>`
  position: relative;

  input {
    padding-left: ${({ theme, region, showPrefixInteraction }) =>
      region ? theme.pxToRem(inputPaddingMap[region] - (showPrefixInteraction ? 0 : 32)) : null};
  }
`

const RegionCodeAndPrefix = styled.button<{ showPrefixInteraction: boolean }>`
  position: absolute;
  height: 3.25rem;
  left: 1.125rem;
  display: flex;
  align-items: center;
  cursor: ${({ showPrefixInteraction }) => (showPrefixInteraction ? 'pointer' : 'default')};
  background-color: transparent;
  border: 0;
  padding: ${({ theme }) => theme.spacing.empty};
  z-index: 1;
`

const Prefix = styled.div`
  ${({ theme }) => theme.typography.input};
  color: ${({ theme }) => theme.arcadeColors.semantic.textStandard};
  margin: 0 ${({ theme }) => theme.spacing.xs};
`

const getGbTemplate = (value: string): string => {
  let normalized = value
  if (normalized.startsWith('44')) normalized = normalized.slice(2)
  else if (normalized.startsWith('0')) normalized = normalized.slice(1)

  if (normalized.startsWith('7')) {
    return '#### ### ####'
  }

  if (normalized.startsWith('20')) {
    return '## #### ####'
  }

  if (/^(1|2|3|5|9)/.test(normalized)) {
    return '##### ######'
  }

  return '###########'
}

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      showRegionCode = false,
      onClickRegionCode,
      placeholder,
      regionCode = 'US',
      regionCodeAriaLabel,
      value,
      ...restProps
    },
    ref
  ) => {
    const phoneRegionConfig = {
      US: {
        prefix: '+1',
        template: '(###) ###-####',
        defaultPlaceholder: '(###) ###-####',
      },
      GB: {
        prefix: '+44',
        template: getGbTemplate(value),
        defaultPlaceholder: '#### ######',
      },
    }

    const showPrefixInteraction = onClickRegionCode != null

    return (
      <InputContainer region={showRegionCode ? regionCode : undefined} showPrefixInteraction={showPrefixInteraction}>
        {showRegionCode ? (
          <RegionCodeAndPrefix
            as={showPrefixInteraction ? 'button' : 'div'}
            onClick={onClickRegionCode}
            type="button"
            aria-label={regionCodeAriaLabel}
            showPrefixInteraction={showPrefixInteraction}
          >
            <Flag countryCode={regionCode} />
            <Prefix className="notranslate">{phoneRegionConfig[regionCode]?.prefix}</Prefix>
            {showPrefixInteraction && <ExpandCenteredIcon variant="prominent" size={24} data-testid="expand-icon" />}
          </RegionCodeAndPrefix>
        ) : null}
        <FormattedInput
          {...restProps}
          placeholder={placeholder || phoneRegionConfig[regionCode]?.defaultPlaceholder}
          ref={ref}
          showTemplateInInput={true}
          template={phoneRegionConfig[regionCode]?.template}
          type="tel"
          value={value}
        />
      </InputContainer>
    )
  }
)

PhoneInput.displayName = 'PhoneInput'

export default PhoneInput
